import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import useAccess from "../../hooks/useAccess";
import AddInfluencersDrawer from "../plans/AddInfluencersDrawer";
import { formatCompact, formatUsername } from "../plans/influencerFormat";
import {
  shareUrlFor,
  teamScope,
  useAddApprovalCreators,
  useApprovalBoard,
  useImportApprovedCreators,
  useRemoveApprovalItem,
  useRotateBrandLink,
  useRotateCreatorLink,
} from "../../hooks/useApprovals";
import ApprovalReviewPanel from "./ApprovalReviewPanel";
import ApprovalWhatsAppSendModal from "./ApprovalWhatsAppSendModal";
import ApprovalStageSwitch from "./ApprovalStageSwitch";
import { videoKind } from "./approvalKinds";
import {
  ApprovalPills,
  CreatorCell,
  LastActivity,
  STATUS_META,
  STATUS_ORDER,
  StatusChip,
  bodyCellSx,
  cardSx,
  copyText,
  errorMessage,
  headCellSx,
  pageSx,
  primaryButtonSx,
  secondaryButtonSx,
  ShareLinkMenu,
} from "./approvalUi";

// One plan's board of one kind (/scripts/:planId, /videos/:planId): its
// creators (username + followers only), where each item stands, the other
// stage's status per creator, share links, and the item drawer.
// `?script=<id>` / `?video=<id>` keeps the open drawer deep-linkable — and is
// how the other board's column links straight into a creator's item.

// The other stage for this creator: its status (opens it), or — on the script
// board, once the script is approved — the one-click start of their video.
const LinkedStageCell = ({ kind, item, planId, onStartVideo, starting }) => {
  const navigate = useNavigate();
  const { sibling } = kind;
  if (item.linked) {
    return (
      <Tooltip title={`Open ${sibling.noun}`} arrow>
        <Box
          component="span"
          onClick={() => navigate(`${sibling.route}/${planId}?${sibling.noun}=${item.linked.id}`)}
          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <StatusChip status={item.linked.status} />
          <ArrowForwardRoundedIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
        </Box>
      </Tooltip>
    );
  }
  if (sibling.importsApprovedFrom === kind && item.status === "approved") {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={starting}
        startIcon={<sibling.Icon size={14} />}
        onClick={() => onStartVideo(item)}
        sx={{ ...secondaryButtonSx, py: 0.25, fontSize: 12.5 }}
      >
        Start {sibling.noun}
      </Button>
    );
  }
  return <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>—</Typography>;
};

const ItemRow = ({ kind, item, planId, selected, onToggle, onOpen, onRotate, onRemove, showSibling, onStartVideo, starting }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const url = shareUrlFor(kind, item.creatorToken);
  return (
    <TableRow hover onClick={() => onOpen(item.id)} selected={selected} sx={{ cursor: "pointer" }}>
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" checked={selected} onChange={() => onToggle(item.id)} />
      </TableCell>
      <TableCell sx={bodyCellSx}><CreatorCell item={item} subtitle={item.removedFromPlan} /></TableCell>
      <TableCell sx={bodyCellSx}>{formatCompact(item.followers)}</TableCell>
      <TableCell sx={bodyCellSx}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
          <StatusChip status={item.status} />
          <ApprovalPills approvals={item.approvals} />
        </Box>
      </TableCell>
      <TableCell sx={bodyCellSx}>{item.currentVersion ? `v${item.currentVersion}` : "—"}</TableCell>
      {showSibling && (
        <TableCell sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
          <LinkedStageCell kind={kind} item={item} planId={planId} onStartVideo={onStartVideo} starting={starting} />
        </TableCell>
      )}
      <TableCell sx={bodyCellSx}><LastActivity at={item.lastActivityAt} by={item.lastActivityBy} /></TableCell>
      <TableCell sx={bodyCellSx} align="right" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Copy creator link" arrow>
          <IconButton size="small" onClick={() => copyText(url, enqueueSnackbar, "Creator link")}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <ShareLinkMenu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          url={url}
          label="Creator link"
          onRotate={() => onRotate(item.id)}
          extraItems={
            <MenuItem onClick={() => { setAnchorEl(null); onRemove(item); }} sx={{ color: "error.main" }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>Remove from board
            </MenuItem>
          }
        />
      </TableCell>
    </TableRow>
  );
};

const ApprovalBoard = ({ kind }) => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const openItemId = searchParams.get(kind.noun);

  const { data, isLoading, error } = useApprovalBoard(kind, planId);
  const addCreators = useAddApprovalCreators(kind, planId);
  const removeItem = useRemoveApprovalItem(kind);
  const rotateCreatorLink = useRotateCreatorLink(kind);
  const rotateBrandLink = useRotateBrandLink(kind, planId);
  // Import into the video board: from the video board's own "Add creators"
  // menu, or a script row's "Start video".
  const importApproved = useImportApprovedCreators(videoKind, planId);

  const [addOpen, setAddOpen] = useState(false);
  const [addMenuEl, setAddMenuEl] = useState(null);
  const [statusTab, setStatusTab] = useState("all");
  const [search, setSearch] = useState("");
  const [brandMenuEl, setBrandMenuEl] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  // A permission-less user doesn't get the other stage's column or shortcuts.
  const { hasPermission } = useAccess();
  const showSibling = hasPermission(kind.sibling.plural);

  const items = useMemo(() => data?.[kind.plural] ?? [], [data, kind]);
  const counts = useMemo(
    () => STATUS_ORDER.reduce((acc, s) => ({ ...acc, [s]: items.filter((x) => x.status === s).length }), {}),
    [items]
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^@/, "");
    return items.filter(
      (s) =>
        (statusTab === "all" || s.status === statusTab) &&
        (!q || (formatUsername(s.username) || "").toLowerCase().includes(q))
    );
  }, [items, statusTab, search]);

  // Drop picks that are filtered out or removed so the count matches what's visible.
  const selectedItems = useMemo(() => visible.filter((s) => selectedIds.has(s.id)), [visible, selectedIds]);
  const allSelected = visible.length > 0 && selectedItems.length === visible.length;
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(visible.map((s) => s.id)));

  // Closing the drawer unmounts the editor, which cancels a running upload.
  const closeDrawer = () => {
    if (uploading && !window.confirm("A video is still uploading. Close and cancel the upload?")) return;
    setOpenItem(null);
  };

  const setOpenItem = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set(kind.noun, id);
    else next.delete(kind.noun);
    setSearchParams(next, { replace: true });
  };

  const notify = (promise, success) =>
    promise
      .then(() => enqueueSnackbar(success, { variant: "success" }))
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));

  const handleRotateBrand = () =>
    notify(rotateBrandLink.mutateAsync(), "Brand link replaced — share the new one");
  const handleRotateCreator = (itemId) =>
    notify(rotateCreatorLink.mutateAsync(itemId), "Creator link replaced — share the new one");
  const handleConfirmRemove = () => {
    const item = pendingRemoval;
    setPendingRemoval(null);
    if (openItemId === item.id) setOpenItem(null);
    notify(removeItem.mutateAsync(item.id), `@${formatUsername(item.username)} removed from the board`);
  };

  const handleImportAll = () => {
    setAddMenuEl(null);
    importApproved
      .mutateAsync()
      .then(({ added, failed }) =>
        enqueueSnackbar(
          added.length
            ? `Added ${added.length} creator${added.length === 1 ? "" : "s"} with approved scripts`
            : failed[0]?.reason || "No creators to add",
          { variant: added.length ? "success" : "info" }
        )
      )
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));
  };

  // Script row -> that creator's video, created on the spot, opened on the video board.
  const handleStartVideo = (item) =>
    importApproved
      .mutateAsync([item.planInfluencerId])
      .then(({ added, failed }) => {
        if (!added[0]) return enqueueSnackbar(failed[0]?.reason || "Couldn't start the video", { variant: "error" });
        navigate(`${videoKind.route}/${planId}?${videoKind.noun}=${added[0].id}`);
      })
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));

  if (isLoading) {
    return (
      <>
        <DashboardHeaderSection hideSearch />
        <Box sx={{ ...pageSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress sx={{ color: "#FF5200" }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DashboardHeaderSection hideSearch />
        <Box sx={pageSx}>
          <Typography sx={{ color: "#B91C1C" }}>{errorMessage(error, `Couldn't load this ${kind.noun} board.`)}</Typography>
        </Box>
      </>
    );
  }

  const { plan, brandToken, importable = 0 } = data;
  const brandUrl = shareUrlFor(kind, brandToken);
  const canImport = Boolean(kind.importsApprovedFrom);

  return (
    <>
      <DashboardHeaderSection hideSearch />
      <Box sx={pageSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <IconButton onClick={() => navigate(kind.route)} sx={{ bgcolor: "#fff", border: "1px solid #E5E7EB" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 700 }}>{plan.name}</Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                {kind.title}{plan.brandName ? ` · ${plan.brandName}` : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            {showSibling && <ApprovalStageSwitch kind={kind} planId={plan.id} />}
            <Button variant="outlined" onClick={() => navigate(`/plans-summary/${plan.id}`)} sx={secondaryButtonSx}>
              Open plan
            </Button>
            <Tooltip title={brandUrl ? `One link for the brand to review every creator's ${kind.noun}` : "Add a creator first"} arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  disabled={!brandUrl}
                  onClick={(e) => setBrandMenuEl(e.currentTarget)}
                  sx={secondaryButtonSx}
                >
                  Brand link
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={importApproved.isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={importApproved.isLoading}
              onClick={(e) => (canImport ? setAddMenuEl(e.currentTarget) : setAddOpen(true))}
              sx={primaryButtonSx}
            >
              Add creators
            </Button>
          </Box>
        </Box>

        {selectedItems.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, px: 2, py: 1, bgcolor: "#fff", border: "1px solid #E5E7EB", borderRadius: "12px" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{selectedItems.length} selected</Typography>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => setWhatsappOpen(true)}
              sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "#25D366", "&:hover": { bgcolor: "#1FAF57" } }}
            >
              Send Message
            </Button>
            <Button onClick={() => setSelectedIds(new Set())} sx={{ textTransform: "none" }}>Clear</Button>
          </Box>
        )}

        <Box sx={cardSx}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2, pt: 1.5 }}>
            <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)} variant="scrollable" sx={{ minHeight: 40 }}>
              <Tab value="all" label={`All (${items.length})`} sx={{ textTransform: "none", minHeight: 40 }} />
              {STATUS_ORDER.map((s) => (
                <Tab key={s} value={s} label={`${STATUS_META[s].label} (${counts[s]})`} sx={{ textTransform: "none", minHeight: 40 }} />
              ))}
            </Tabs>
            <TextField
              size="small"
              placeholder="Search username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 240 }, "& .MuiInputBase-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedItems.length > 0 && !allSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Username</TableCell>
                  <TableCell sx={headCellSx}>Followers</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Version</TableCell>
                  {showSibling && <TableCell sx={headCellSx}>{kind.sibling.Noun}</TableCell>}
                  <TableCell sx={headCellSx}>Last activity</TableCell>
                  <TableCell sx={headCellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showSibling ? 8 : 7} align="center" sx={{ py: 8, border: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {items.length === 0 ? "No creators on this board yet" : "No creators match"}
                      </Typography>
                      {items.length === 0 && (
                        <Typography variant="body2" color="text.secondary">{kind.copy.boardEmptyHint}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((item) => (
                    <ItemRow
                      key={item.id}
                      kind={kind}
                      item={item}
                      planId={plan.id}
                      selected={selectedIds.has(item.id)}
                      onToggle={toggleOne}
                      onOpen={setOpenItem}
                      onRotate={handleRotateCreator}
                      onRemove={setPendingRemoval}
                      showSibling={showSibling}
                      onStartVideo={handleStartVideo}
                      starting={importApproved.isLoading}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>

      <ShareLinkMenu
        anchorEl={brandMenuEl}
        onClose={() => setBrandMenuEl(null)}
        url={brandUrl}
        label="Brand link"
        onRotate={handleRotateBrand}
      />

      {canImport && (
        <Menu anchorEl={addMenuEl} open={Boolean(addMenuEl)} onClose={() => setAddMenuEl(null)}>
          <MenuItem onClick={handleImportAll} disabled={!importable}>
            <ListItemIcon><TaskAltRoundedIcon fontSize="small" sx={{ color: STATUS_META.approved.dot }} /></ListItemIcon>
            <ListItemText
              primary={`Creators with approved ${kind.importsApprovedFrom.plural} (${importable})`}
              secondary={importable ? "Everyone the brand signed off at the script stage" : "Everyone with an approved script is already here"}
            />
          </MenuItem>
          <MenuItem onClick={() => { setAddMenuEl(null); setAddOpen(true); }}>
            <ListItemIcon><ContentPasteRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Paste usernames" secondary="Any creator from this plan's lists" />
          </MenuItem>
        </Menu>
      )}

      <ApprovalWhatsAppSendModal
        open={whatsappOpen}
        onClose={() => setWhatsappOpen(false)}
        kind={kind}
        planId={plan.id}
        planName={plan.name}
        brandName={plan.brandName}
        selectedItems={selectedItems}
      />

      <AddInfluencersDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        lists={plan.lists}
        planId={plan.id}
        mutation={addCreators}
        title="Add creators"
      />

      <Drawer
        anchor="right"
        open={Boolean(openItemId)}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: isMobile ? "100%" : { sm: 560, md: 680 }, maxWidth: "100vw" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{kind.Noun}</Typography>
          <IconButton size="small" onClick={closeDrawer}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto" }}>
          {openItemId && (
            <ApprovalReviewPanel scope={teamScope(kind, planId)} itemId={openItemId} viewer="team" onUploadingChange={setUploading} />
          )}
        </Box>
      </Drawer>

      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove from the board?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            @{formatUsername(pendingRemoval?.username)}'s {kind.noun}, its history and their share link will stop being
            available. The creator stays in the plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPendingRemoval(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove} sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApprovalBoard;
